// @ts-check
import React, { Component } from 'react';
import { Page, Row, Widget, TabbedWidget } from 'shared/containers/portal_layout';
import LoadingContent from 'shared/components/LoadingContent';
import actions from './actions';
import { Link } from 'react-router';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import TabPane from 'shared/components/TabPane';
import styles from './Show.module.scss';
import PromiseButton from 'shared/components/PromiseButton';

class Show extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = { hotlistings: null, whitelistings: null, blacklistings: null };
    this.refresh = this.refresh.bind(this);
    this.createHotlisting = this.createHotlisting.bind(this);
    this.removeHotlisting = this.removeHotlisting.bind(this);
    this.createWhitelisting = this.createWhitelisting.bind(this);
    this.removeWhitelisting = this.removeWhitelisting.bind(this);
    this.createBlacklisting = this.createBlacklisting.bind(this);
    this.removeBlacklisting = this.removeBlacklisting.bind(this);
  }

  componentDidMount() {
    this.refresh();
  }

  refresh() {
    actions.fetchNetwork().then(data => this.setState({ ...data }));
  }

  defaultErrorHandler(error) {
    const response = error.response;
    if (response.data.fullErrorMessages) {
      response.data.fullErrorMessages.forEach(message => {
        FlashMessageActions.flashError({ message });
      });
    }
  }

  createHotlisting(providerId) {
    return actions.createHotlisting(providerId).then(response => {
      FlashMessageActions.flashSuccess({ message: 'Provider added to Hotlist' });
      this.refresh();
    }, this.defaultErrorHandler);
  }

  removeHotlisting(hotlisting) {
    /* eslint-disable no-alert */
    if (
      window.confirm(
        `Are you sure you want to remove ${hotlisting.listedProviderName} from your hotlist?`
      )
    ) {
      return actions.removeHotlisting(hotlisting.id).then(response => {
        FlashMessageActions.flashSuccess({ message: 'Provider removed from Hotlist' });
        this.refresh();
      });
    }
    return Promise.resolve();
    /* eslint-enable no-alert */
  }

  createWhitelisting(providerId) {
    return actions.createWhitelisting(providerId).then(response => {
      FlashMessageActions.flashSuccess({ message: 'Provider added to Whitelist' });
      this.refresh();
    }, this.defaultErrorHandler);
  }

  removeWhitelisting(whitelisting) {
    /* eslint-disable no-alert */
    if (
      window.confirm(
        `Are you sure you want to remove ${whitelisting.listedProviderName} from your whitelist?`
      )
    ) {
      return actions.removeWhitelisting(whitelisting.id).then(response => {
        FlashMessageActions.flashSuccess({ message: 'Provider removed from Whitelist' });
        this.refresh();
      });
    }
    return Promise.resolve();
    /* eslint-enable no-alert */
  }

  createBlacklisting(providerId) {
    return actions.createBlacklisting(providerId).then(response => {
      FlashMessageActions.flashSuccess({ message: 'Provider added to restricted list' });
      this.refresh();
    }, this.defaultErrorHandler);
  }

  removeBlacklisting(blacklisting) {
    /* eslint-disable no-alert */
    if (
      window.confirm(
        `Are you sure you want to remove ${blacklisting.listedProviderName} from your restricted list?`
      )
    ) {
      return actions.removeBlacklisting(blacklisting.id).then(response => {
        FlashMessageActions.flashSuccess({ message: 'Provider removed from restricted list' });
        this.refresh();
      });
    }
    return Promise.resolve();
    /* eslint-enable no-alert */
  }

  render() {
    if (!this.state.hotlistings) return <LoadingContent />;
    return (
      <PresentationComponent
        {...this.state}
        createHotlisting={this.createHotlisting}
        removeHotlisting={this.removeHotlisting}
        createWhitelisting={this.createWhitelisting}
        removeWhitelisting={this.removeWhitelisting}
        createBlacklisting={this.createBlacklisting}
        removeBlacklisting={this.removeBlacklisting}
      />
    );
  }
}
export default Show;

const PresentationComponent = props => (
  <Page title="My Network">
    <Row>
      <span className="span6">
        <Row>
          <Widget icon="pencil" title="My Networks" span={12}>
            <div className="well">
              <p>You are not currently part of any networks.</p>
              <p>Networks can include ACOs, HMOs, IPAs, or any other provider network.</p>
            </div>
          </Widget>
        </Row>
        <Row>
          <NearbyProviders {...props} />
        </Row>
      </span>
      <span className="span6">
        <Row>
          <ListingsTabs {...props} />
        </Row>
      </span>
    </Row>
  </Page>
);

const List = ({ listName, listings, removeListing, createListing }) => (
  <div className={styles.listings} data-spec-id={listName.toLowerCase()}>
    {listings.length > 0 ? (
      <table className="table table-bordered" id={`current_${listName}`}>
        <tbody>
          {listings.map(listing => (
            <tr key={listing.id}>
              <td>
                <Link to={`/a/portal/providers/${listing.listedProviderId}/profile`}>
                  {listing.listedProviderName}
                </Link>
              </td>
              <td>
                <PromiseButton
                  onClick={() => removeListing(listing)}
                  className="btn btn-mini btn-danger"
                >
                  remove from {listName}
                </PromiseButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div className="well">
        No providers on your {listName}. You can manage your network and priority lists on this
        page.
      </div>
    )}
  </div>
);

const AddToList = ({ provider, listName, createListing, listedProviderIds, buttonClass }) => (
  <td>
    {listedProviderIds.indexOf(provider.id) >= 0 ? (
      <span className="icon-check" title={`On ${listName}`} />
    ) : (
      <PromiseButton
        type="button"
        onClick={() => createListing(provider.id)}
        className={`btn btn-mini ${buttonClass}`}
        id={`${listName.toLowerCase()}_${provider.id}`}
        title={`Add to ${listName}`}
      >
        Add
      </PromiseButton>
    )}
  </td>
);

const NearbyProviders = props => {
  const {
    hotlistings,
    whitelistings,
    blacklistings,
    nearbys,
    createHotlisting,
    createBlacklisting,
    createWhitelisting,
  } = props;
  const hotlistedProviderIds = hotlistings.map(hotlisting => hotlisting.listedProviderId);
  const whitelistedProviderIds = whitelistings.map(whitelisting => whitelisting.listedProviderId);
  const blacklistedProviderIds = blacklistings.map(blacklisting => blacklisting.listedProviderId);
  return (
    <Widget span={12} icon="pencil" title="Nearby Providers">
      <div className="well">
        <p>
          Your relationships are important. To make sure we help reflect your relationships, you can
          add providers to your Hotlist, Whitelist, or Restricted Access List below.
        </p>
        <p>These lists are designed to:</p>
        <ul>
          <li>
            <b>Hotlist:</b> these are providers you like to refer to, and want shown at the top of
            the results screen for fast referral access
          </li>
          <li>
            <b>Whitelist:</b> as a specialist, these are the providers you want to have nearly
            full-access to your availability, regardless of the specifics of the referral.
          </li>
          <li>
            <b>Restricted Access List:</b> as a specialist, these are the providers you would prefer
            not to receive referrals from
          </li>
        </ul>
        <p>
          To add a provider not shown below to your Hotlist, Whitelist, or Restricted Access List,
          shoot us an email at <a href="mailto:support@preferral.com">support@preferral.com</a>.
        </p>
      </div>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Providers</th>
            <th className="hidden-phone hidden-tablet">Specialties</th>
            <th>Hotlist</th>
            <th>Whitelist</th>
            <th>Restricted</th>
          </tr>
        </thead>
        <tbody>
          {nearbys.map(provider => (
            <tr key={provider.id}>
              <td>
                <Link to={`/a/portal/providers/${provider.id}/profile`}>{provider.name}</Link>
              </td>
              <td className="hidden-phone hidden-tablet">
                {provider.specialtyNames.map(name => (
                  <div key={name}>{name}</div>
                ))}
              </td>
              <AddToList
                listName="Hotlist"
                provider={provider}
                listedProviderIds={hotlistedProviderIds}
                createListing={createHotlisting}
                buttonClass="btn-primary"
              />
              <AddToList
                listName="Whitelist"
                provider={provider}
                listedProviderIds={whitelistedProviderIds}
                createListing={createWhitelisting}
                buttonClass="btn-success"
              />
              <AddToList
                listName="Blacklist"
                provider={provider}
                listedProviderIds={blacklistedProviderIds}
                createListing={createBlacklisting}
                buttonClass="btn-inverse"
              />
            </tr>
          ))}
        </tbody>
      </table>
    </Widget>
  );
};

const ListingsTabs = props => {
  const {
    hotlistings,
    whitelistings,
    blacklistings,
    removeHotlisting,
    removeWhitelisting,
    removeBlacklisting,
  } = props;
  return (
    <TabbedWidget span={12} id="hotlistings">
      <TabPane tabName="My Hotlist">
        <List listName="hotlist" listings={hotlistings} removeListing={removeHotlisting} />
      </TabPane>
      <TabPane tabName="My Whitelist">
        <List listName="whitelist" listings={whitelistings} removeListing={removeWhitelisting} />
      </TabPane>
      <TabPane tabName="Restricted">
        <List
          listName="restricted list"
          listings={blacklistings}
          removeListing={removeBlacklisting}
        />
      </TabPane>
    </TabbedWidget>
  );
};
